import { getWeek, isSameMonth } from 'date-fns';

import { availableLocaleConfigs } from '~src/commons/utils/translations/localization';

import { IntervalFormattingFunction, addDaysToNow, getDeliveryWeeks } from '.';

const WEEK_LENGTH = 7;

export const formatDays: IntervalFormattingFunction = ({ deliveryTime, translate }) => {
  const { min, max, unit } = deliveryTime;

  return translate(
    unit === 'business_day'
      ? 'widget.delivery_time.INTERVAL_BUSINESS_DAY_DAYS'
      : 'widget.delivery_time.INTERVAL_DAY_DAYS',
    { min, max }
  );
};

export const formatDate: IntervalFormattingFunction = ({ deliveryTime, translate, format }) => {
  const { min, max } = deliveryTime;
  if (isSameMonth(addDaysToNow(min), addDaysToNow(max))) {
    return translate('widget.delivery_time.INTERVAL_DATE', {
      dayOfMonthMin: format(
        addDaysToNow(min),
        translate('widget.delivery_time.DAY_OF_MONTH_FORMAT')
      ),
      dayOfMonthMax: format(
        addDaysToNow(max),
        translate('widget.delivery_time.DAY_OF_MONTH_FORMAT')
      ),
      INTERVAL_MONTH_NUMBER_MAX: translate(
        `widget.delivery_time.INTERVAL_MONTH_NUMBER_${format(addDaysToNow(max), 'M')}`
      ),
    });
  } else {
    const monthMin = format(addDaysToNow(min), 'M');
    const monthMax = format(addDaysToNow(max), 'M');
    return translate('widget.delivery_time.INTERVAL_DATE_NEXT_MONTH', {
      min: format(addDaysToNow(min), translate('widget.delivery_time.DAY_OF_MONTH_FORMAT')),
      INTERVAL_MONTH_NUMBER_MIN: translate(
        `widget.delivery_time.INTERVAL_MONTH_NUMBER_${monthMin}`
      ),
      max: format(addDaysToNow(max), translate('widget.delivery_time.DAY_OF_MONTH_FORMAT')),
      INTERVAL_MONTH_NUMBER_MAX: translate(
        `widget.delivery_time.INTERVAL_MONTH_NUMBER_${monthMax}`
      ),
    });
  }
};

export const formatWeekday: IntervalFormattingFunction = ({ deliveryTime, translate, format }) => {
  const { min, max } = deliveryTime;

  const minNumericWeekDay = format(addDaysToNow(min), 'i');
  const maxNumericWeekDay = format(addDaysToNow(max), 'i');
  if (max < WEEK_LENGTH) {
    return translate('widget.delivery_time.INTERVAL_WEEKDAY', {
      INTERVAL_WEEKDAY_NUMBER_MIN: translate(
        `widget.delivery_time.INTERVAL_WEEKDAY_NUMBER_${minNumericWeekDay}`
      ),
      INTERVAL_WEEKDAY_NUMBER_MAX: translate(
        `widget.delivery_time.INTERVAL_WEEKDAY_NUMBER_${maxNumericWeekDay}`
      ),
    });
  } else {
    return translate('widget.delivery_time.INTERVAL_WEEKDAY_DATE', {
      INTERVAL_WEEKDAY_NUMBER_MIN: translate(
        `widget.delivery_time.INTERVAL_WEEKDAY_NUMBER_${minNumericWeekDay}`
      ),
      dateMin: format(addDaysToNow(min), translate('widget.delivery_time.WEEKDAY_DATE_FORMAT')),
      INTERVAL_WEEKDAY_NUMBER_MAX: translate(
        `widget.delivery_time.INTERVAL_WEEKDAY_NUMBER_${maxNumericWeekDay}`
      ),
      dateMax: format(addDaysToNow(max), translate('widget.delivery_time.WEEKDAY_DATE_FORMAT')),
    });
  }
};

export const formatWeekdayDate: IntervalFormattingFunction = ({
  deliveryTime,
  translate,
  format,
}) => {
  const { min, max } = deliveryTime;

  return translate('widget.delivery_time.INTERVAL_WEEKDAY_DATE', {
    INTERVAL_WEEKDAY_NUMBER_MIN: translate(
      `widget.delivery_time.INTERVAL_WEEKDAY_NUMBER_${format(addDaysToNow(min), 'i')}`
    ),
    dateMin: format(addDaysToNow(min), translate('widget.delivery_time.WEEKDAY_DATE_FORMAT')),
    INTERVAL_WEEKDAY_NUMBER_MAX: translate(
      `widget.delivery_time.INTERVAL_WEEKDAY_NUMBER_${format(addDaysToNow(max), 'i')}`
    ),
    dateMax: format(addDaysToNow(max), translate('widget.delivery_time.WEEKDAY_DATE_FORMAT')),
  });
};

export const formatWeeks: IntervalFormattingFunction = ({ deliveryTime, translate, locale }) => {
  const { deliveryWeekMin, deliveryWeekMax } = getDeliveryWeeks(deliveryTime, locale);

  return translate('widget.delivery_time.INTERVAL_WEEKS', {
    min: deliveryWeekMin,
    max: deliveryWeekMax,
  });
};

export const formatWeekNumbers: IntervalFormattingFunction = ({
  deliveryTime,
  translate,
  locale,
}) => {
  const { min, max } = deliveryTime;
  const minDeliveryDate = addDaysToNow(min);
  const maxDeliveryDate = addDaysToNow(max);

  const localeConfig = availableLocaleConfigs[locale];

  return translate('widget.delivery_time.INTERVAL_WEEK_NUMBER', {
    min: getWeek(minDeliveryDate, { locale: localeConfig }),
    max: getWeek(maxDeliveryDate, { locale: localeConfig }),
  });
};
